import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select, Space, Spin, Typography } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useTrackDhlShipments } from "../../query/Tracking";

const TrackingList = () => {
  const [trackingInput, setTrackingInput] = React.useState<string>("");
  const [courier, setCourier] = React.useState<string>("dhl");
  const [isSearching, setIsSearching] = React.useState(false);
  const [invalidNumbers, setInvalidNumbers] = React.useState<string[]>([]);

  const validateAndFilterNumbers = React.useCallback(
    (input: string) => {
      const numbers = input
        .split(/[\n,]/)
        .map((num) => num.trim())
        .filter((num) => num.length > 0);
      const valid: string[] = [];
      const invalid: string[] = [];

      numbers.forEach((num) => {
        if (courier === "dhl") {
          if (/^\d{10}$/.test(num)) {
            valid.push(num);
          } else {
            invalid.push(num);
          }
        } else {
          valid.push(num);
        }
      });

      setInvalidNumbers(invalid);
      return valid;
    },
    [courier]
  );

  const trackingNumbers = React.useMemo(() => {
    return validateAndFilterNumbers(trackingInput);
  }, [trackingInput, validateAndFilterNumbers]);

  const {
    data: dhlData,
    isLoading: isDhlLoading,
    refetch: refetchDhl,
    error: dhlError,
  } = useTrackDhlShipments(isSearching ? trackingNumbers : []);

  const handleCourierChange = (value: string) => {
    setCourier(value);
  };

  const handleSearch = () => {
    if (trackingNumbers.length > 0) {
      setIsSearching(true);
      refetchDhl();
    }
  };

  return (
    <div className="p-4">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Typography.Title
          style={{
            textTransform: "uppercase",
            margin: 0,
            fontFamily: "Chakra Petch",
          }}
          level={3}
          className="dark:text-white"
        >
          Tra cứu vận đơn theo danh sách
        </Typography.Title>
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={24} lg={6}>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
            <Typography.Text strong className="block mb-2">
              Nhà vận chuyển
            </Typography.Text>
            <Select
              value={courier}
              onChange={handleCourierChange}
              style={{ width: "100%" }}
              options={[{ value: "dhl", label: "DHL" }]}
            />
            <Typography.Text strong className="block mt-4 mb-2">
              Danh sách mã vận đơn
            </Typography.Text>
            <Input.TextArea
              placeholder="Nhập danh sách mã vận đơn (mỗi mã một dòng)"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              style={{ width: "100%", marginBottom: 16 }}
              rows={10}
              allowClear
            />
            <div className="mb-2">
              <Typography.Text type="secondary">
                {trackingNumbers.length} mã vận đơn hợp lệ
              </Typography.Text>
              {invalidNumbers.length > 0 && (
                <div className="mt-2">
                  <Typography.Text type="danger" className="block">
                    {invalidNumbers.length} mã vận đơn không hợp lệ:
                  </Typography.Text>
                  <Typography.Text type="danger" className="block text-sm">
                    {courier === "dhl"
                      ? "(Mã vận đơn DHL phải đúng 10 chữ số)"
                      : ""}
                  </Typography.Text>
                  <div className="mt-1 text-red-500 text-sm max-h-20 overflow-y-auto">
                    {invalidNumbers.join(", ")}
                  </div>
                </div>
              )}
              {dhlError && (
                <div className="mt-2">
                  <Typography.Text type="danger" className="block">
                    Đã xảy ra lỗi khi tra cứu vận đơn. Vui lòng thử lại.
                  </Typography.Text>
                </div>
              )}
            </div>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                onClick={handleSearch}
                block
                loading={isDhlLoading}
                disabled={trackingNumbers.length === 0 || isDhlLoading}
                icon={<SearchOutlined />}
              >
                Tra cứu
              </Button>
            </Space>
          </div>
        </Col>

        <Col xs={24} md={24} lg={18}>
          <div
            className="overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 200px)", paddingRight: 8 }}
          >
            <Row gutter={[14, 16]}>
              {isDhlLoading ? (
                <Col span={24}>
                  <div className="flex justify-center items-center h-48">
                    <Spin indicator={<LoadingOutlined spin />} size="large" />
                  </div>
                </Col>
              ) : (
                courier === "dhl" &&
                dhlData?.map((shipmentData, index) => (
                  <Col xs={24} key={index}>
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-y-auto">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <Typography.Title
                            level={5}
                            style={{
                              margin: 0,
                              fontFamily: "Chakra Petch",
                              textTransform: "uppercase",
                            }}
                          >
                            Mã vận đơn :{" "}
                            {shipmentData.shipments?.[0]?.id ||
                              "Lỗi - Vui lòng kiểm tra lại mã vận đơn hoặc thử tra cứu lại sau"}
                          </Typography.Title>
                        </div>
                        <div>
                          {shipmentData.shipments?.[0]?.events?.[0] && (
                            <Typography.Text
                              style={{ fontFamily: "Chakra Petch" }}
                              type="secondary"
                              className="block mt-2"
                            >
                              Cập nhật lúc:{" "}
                              {dayjs(
                                shipmentData.shipments[0].events[0].timestamp
                              ).format("DD/MM/YYYY HH:mm")}
                            </Typography.Text>
                          )}
                        </div>
                      </div>
                      <div>
                        <Typography.Text
                          style={{ fontFamily: "Chakra Petch" }}
                          className="block"
                        >
                          Trạng thái :{" "}
                          {shipmentData.shipments?.[0]?.status?.description ||
                            "Không có thông tin"}
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>
                ))
              )}
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default TrackingList;
